import { Box, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { APIProvider } from '@vis.gl/react-google-maps';
import { Amplify } from 'aws-amplify';
import { memo, useEffect } from 'react';
import { Freeze } from 'react-freeze';
import { useLocation } from 'react-router-dom';

import { LoadingIndicator } from '@/components/common/LoadingIndicator';
import { AppThemeProvider } from '@/context/AppThemeProvider';
import { AuthProvider, useAuthContext } from '@/context/AuthProvider';
import { ProcessingJobTrackerProvider } from '@/context/ProcessingJobTrackerProvider';
import { PropertiesProvider } from '@/context/PropertiesProvider';
import { RFQCompileProvider } from '@/context/RFQCompileProvider';
import { SnackbarProvider } from '@/context/SnackbarProvider';
import { UploaderProvider } from '@/context/UploaderProvider';
import { WebSocketProvider } from '@/context/WebSocketProvider';
import { Router } from '@/routes/Router';
import { setupApiClientInterceptors } from '@/services/api';
// import { initializeHotjar } from '@/utils/hotjar';

Amplify.configure({
  Auth: {
    Cognito: {
      identityPoolId: import.meta.env.VITE_IDENTITY_POOL_ID,
      userPoolClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID,
      userPoolId: import.meta.env.VITE_USER_POOL_ID,
      loginWith: {
        oauth: {
          domain: import.meta.env.VITE_COGNITO_DOMAIN?.replace(/^https?:\/\//, ''),
          scopes: ['email', 'profile', 'openid', 'aws.cognito.signin.user.admin'],
          redirectSignIn: [`${window.location.origin}/oauth-callback`],
          redirectSignOut: [`${window.location.origin}/login`],
          responseType: 'code',
        },
      },
    },
  },
});

// initializeHotjar({ siteId: 5345282, hotjarVersion: 6 });

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 30, // 30 minutes
      retry: 2,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10000),
    },
    mutations: {
      retry: false,
    },
  },
});

const AppErrorFallback = () => (
  <Box
    sx={{
      p: 4,
      textAlign: 'center',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
    <Typography variant="h5" gutterBottom>
      Something went wrong
    </Typography>
    <Typography variant="body1" color="text.secondary">
      The team has been notified. Please refresh the page.
    </Typography>
  </Box>
);

const AppContent = memo(() => {
  const { isAuthInitialized, refreshAuthSession, logOut } = useAuthContext();
  const location = useLocation();

  // Public RFQ share pages must render without waiting for Cognito init —
  // contractors don't have an account.
  const isPublicRoute = location.pathname.startsWith('/rfq/share/');

  useEffect(() => {
    setupApiClientInterceptors({
      refreshAuthSession,
      logOut,
    });
  }, []);

  return (
    <Freeze
      freeze={!isPublicRoute && !isAuthInitialized}
      placeholder={<LoadingIndicator fullScreen />}>
      <PropertiesProvider>
        <UploaderProvider>
          <ProcessingJobTrackerProvider>
            <RFQCompileProvider>
              <Router />
            </RFQCompileProvider>
          </ProcessingJobTrackerProvider>
        </UploaderProvider>
      </PropertiesProvider>
    </Freeze>
  );
});

function App() {
  return (
    // Always render APIProvider so useMapsLibrary/useMap hooks never throw
    // "missing provider". When the key is absent the Maps script fails to load
    // and all useMapsLibrary calls return null — components degrade gracefully.
    <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? ''}>
      <QueryClientProvider client={queryClient}>
        <AppThemeProvider>
          <SnackbarProvider>
            <Sentry.ErrorBoundary fallback={<AppErrorFallback />} showDialog>
              <AuthProvider>
                <WebSocketProvider>
                  <AppContent />
                </WebSocketProvider>
              </AuthProvider>
            </Sentry.ErrorBoundary>
          </SnackbarProvider>
        </AppThemeProvider>
      </QueryClientProvider>
    </APIProvider>
  );
}

export default App;
